// src/utils/auth.js
// ✅ Centrale auth helper — decodeert JWT, beheert vernieuwing en headers

const resolveApiBase = () => {
  const configured = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");
  if (configured) return configured;

  if (typeof window !== "undefined") {
    const { protocol, hostname, origin } = window.location;
    const isLocalHost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      /^192\.168\.\d{1,3}\.\d{1,3}$/i.test(hostname);

    return isLocalHost ? `${protocol}//${hostname}:5000` : origin;
  }

  return "http://localhost:5000";
};

const renewApi = {
  post: async (path, body = {}, options = {}) => {
    const normalizedPath = path.startsWith("/api/") ? path : `/api${path}`;
    const response = await fetch(`${resolveApiBase()}${normalizedPath}`, {
      method: "POST",
      credentials: options.withCredentials === false ? "same-origin" : "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      body: JSON.stringify(body || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data?.error || data?.message || "Request failed");
      error.response = { status: response.status, data };
      throw error;
    }
    return { data };
  },
};

let refreshTokenPromise = null;

// 🔐 Token ophalen
export function getToken() {
  try {
    return sessionStorage.getItem("jwt") || null;
  } catch {
    return null;
  }
}

// 🔐 Token opslaan of verwijderen
export function setToken(token) {
  try {
    if (token) {
      sessionStorage.setItem("jwt", token);
    } else {
      sessionStorage.removeItem("jwt");
      localStorage.removeItem("jwt");
    }
  } catch (err) {
    console.warn("⚠️ Kan JWT niet opslaan:", err);
  }
}

// 🚪 Uitloggen / opschonen
export function storeAuthSession(token, user) {
  setToken(token);

  if (!user) return;

  try {
    localStorage.setItem("user", JSON.stringify(user));
    if (user.email) localStorage.setItem("userEmail", user.email);
    if (user.role) localStorage.setItem("userRole", user.role);
  } catch (err) {
    console.warn("Kan gebruikerssessie niet opslaan:", err);
  }
}

export function clearAuth() {
  try {
    sessionStorage.removeItem("jwt");
    localStorage.removeItem("jwt");
    localStorage.removeItem("user");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userRole");
  } catch {
    /* negeren */
  }
}

// 👤 Huidige gebruiker decoderen uit JWT
export function getCurrentUser() {
  const token = getToken();
  if (!token) return null;

  try {
    const [, payloadBase64] = token.split(".");
    const payloadJson = atob(payloadBase64);
    const decoded = JSON.parse(payloadJson);

    return {
      id: decoded.sub || null,
      email: decoded.email || null,
      role: decoded.role || null,
      companyName: decoded.companyName || null,
      companyGroupName: decoded.companyGroupName || null,
      companyDomain: decoded.companyDomain || null,
      exp: decoded.exp || null,
    };
  } catch (err) {
    console.warn("⚠️ Ongeldige of verlopen JWT:", err);
    clearAuth();
    return null;
  }
}

// 📆 Controleer of token bijna verlopen is (binnen 24 uur)
function isTokenExpiringSoon(decoded) {
  if (!decoded?.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  const timeLeft = decoded.exp - now;
  return timeLeft < 24 * 60 * 60; // minder dan 24 uur geldig
}

// ♻️ Token automatisch vernieuwen via backend
export async function refreshTokenIfNeeded() {
  const token = getToken();
  if (!token) return null;

  const user = getCurrentUser();
  if (!user || !isTokenExpiringSoon(user)) return token;

  if (refreshTokenPromise) return refreshTokenPromise;

  try {
    console.log("♻️ JWT verloopt binnenkort — vernieuwen...");
    refreshTokenPromise = renewApi.post("/renew-token", {}, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    const res = await refreshTokenPromise;

    if (res.data?.success && res.data?.token) {
      setToken(res.data.token);
      console.log("✅ Nieuw JWT-token ontvangen en opgeslagen");
      return res.data.token;
    } else {
      console.warn("⚠️ Geen geldig vernieuwd token ontvangen");
      return token;
    }
  } catch (err) {
    console.error("❌ Fout bij vernieuwen token:", err);
    return token;
  } finally {
    refreshTokenPromise = null;
  }
}

// 🧱 Helper voor Authorization-header
export async function getAuthHeaders() {
  const token = await refreshTokenIfNeeded();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// 🕒 Optioneel: periodieke tokencheck (elke 6 uur)
setInterval(() => {
  refreshTokenIfNeeded().catch(() => {});
}, 6 * 60 * 60 * 1000); // elke 6 uur
